import { createFileRoute } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ChevronLeft, Play } from "lucide-react";
import { FiligreeFrame } from "@/components/brand/FiligreeFrame";
import { Eyebrow } from "@/components/brand/Eyebrow";
import { Seal } from "@/components/brand/Seal";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { SecondaryButton } from "@/components/brand/SecondaryButton";
import { AdvisorFitQuiz } from "@/components/careers/AdvisorFitQuiz";
import { CompensationCalculator } from "@/components/careers/CompensationCalculator";
import { supabase } from "@/integrations/supabase/client";
import { seo } from "@/lib/seo";

export const Route = createFileRoute("/careers")({
  head: () =>
    seo({
      title: "Become a Family Wealth Strategist & Economic Empowerment Coach · Currency Thoughts",
      description:
        "Join a Family Wealth Strategy Firm. Become a Family Wealth Strategist and Economic Empowerment Coach. Veteran-led, family-first, infrastructure-built.",
      canonical: "/careers",
    }),
  component: CareersPage,
});

function CareersPage() {
  useEmailUtmTracking();
  return (
    <>
      <Hero />
      <StatStrip />
      <QuizSection />
      <WelcomeVideoSection />
      <MeetFounderCard />
      <FoundersLetter />
      <AboutOpportunity />
      <CompensationCalculator />
    </>
  );
}

function useEmailUtmTracking() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("utm_source") !== "email") return;
    const campaign = params.get("utm_campaign") || "unknown";
    const flagKey = `ct.career_click_logged.${campaign}`;
    if (sessionStorage.getItem(flagKey)) return;
    sessionStorage.setItem(flagKey, "1");
    void (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      await supabase.from("activity_logs").insert({
        user_id: user?.id ?? null,
        action: "client.career_link_clicked",
        target_type: "page",
        target_id: "careers",
        metadata: { source: "email", campaign },
      });
    })();
  }, []);
}

type Founder = {
  founder_name: string;
  founder_role: string;
  founder_headshot_url: string | null;
  founder_signature_url: string | null;
  founder_short_bio: string | null;
  founder_bio_markdown: string | null;
  welcome_video_url: string | null;
  welcome_video_poster_url: string | null;
};

function useFounder() {
  const [f, setF] = useState<Founder | null>(null);
  const load = () =>
    supabase.from("founder_content").select("*").limit(1).maybeSingle()
      .then(({ data }) => { if (data) setF(data as Founder); });
  useEffect(() => {
    void load();
    const channelName = `founder_content-careers-${Math.random().toString(36).slice(2, 10)}`;
    const channel = supabase
      .channel(channelName)
      .on("postgres_changes", { event: "*", schema: "public", table: "founder_content" }, () => { void load(); })
      .subscribe();
    return () => { void supabase.removeChannel(channel); };
  }, []);
  return f;
}

function WelcomeVideoSection() {
  const f = useFounder();
  if (!f?.welcome_video_url) return null;
  return (
    <section className="bg-cream-50 py-20 md:py-24 border-t border-bronze-600/20">
      <div className="container mx-auto px-6 md:px-10 max-w-4xl">
        <Eyebrow align="center">A Word From the Founder</Eyebrow>
        <h2 className="mt-4 text-center font-display font-semibold text-navy-800 text-[32px] md:text-[40px]">
          Hear it <em className="text-bronze-600 italic font-normal">straight</em>.
        </h2>
        <div className="mt-10 ring-2 ring-bronze-600/60 bg-navy-900 rounded-sm overflow-hidden shadow-[0_30px_80px_-40px_rgba(0,0,0,0.4)]">
          <video
            className="w-full aspect-video"
            controls
            preload="metadata"
            poster={f.welcome_video_poster_url ?? undefined}
            src={f.welcome_video_url}
          />
        </div>
        <div className="mt-10 flex justify-center">
          <PrimaryButton asChild size="lg">
            <Link to="/careers/apply">Apply Now</Link>
          </PrimaryButton>
        </div>
      </div>
    </section>
  );
}

function MeetFounderCard() {
  const f = useFounder();
  if (!f || (!f.founder_short_bio && !f.founder_headshot_url)) return null;
  return (
    <section className="bg-cream-50 py-16 border-t border-bronze-600/20">
      <div className="container mx-auto px-6 md:px-10 max-w-3xl">
        <div className="bg-white border border-bronze-600/30 rounded-sm p-8 md:p-10 flex items-center gap-8 shadow-[0_20px_60px_-40px_rgba(168,139,92,0.4)]">
          {f.founder_headshot_url && (
            <img
              src={f.founder_headshot_url}
              alt={f.founder_name}
              className="h-28 w-28 object-cover ring-2 ring-bronze-600/50 shrink-0"
            />
          )}
          <div>
            <Eyebrow>Meet the Founder</Eyebrow>
            <h3 className="mt-2 font-display text-navy-800 text-[22px]">{f.founder_name}</h3>
            <p className="text-bronze-600 font-sans text-[11px] uppercase tracking-[0.2em]">{f.founder_role}</p>
            {f.founder_short_bio && (
              <p className="mt-3 font-sans text-[15px] text-charcoal-900 leading-relaxed">{f.founder_short_bio}</p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Hero() {
  return (
    <section className="bg-navy-900 text-cream-50 relative">
      <FiligreeFrame intensity="subtle" className="min-h-[90vh] flex items-center">
        <div className="container mx-auto px-6 md:px-10 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-12 lg:gap-16 items-center w-full">
          <div>
            <Link
              to="/"
              className="mb-6 inline-flex items-center gap-1.5 font-sans text-[12px] font-semibold uppercase tracking-[0.18em] text-bronze-400 hover:text-cream-50 transition-colors"
            >
              <ChevronLeft size={14} strokeWidth={2.5} aria-hidden />
              <span>Back to home</span>
            </Link>
            <Eyebrow color="cream">Become a Family Wealth Strategist</Eyebrow>
            <h1 className="mt-6 font-display font-bold text-cream-50 text-[40px] md:text-[56px] lg:text-[64px] leading-[1.05] tracking-tight">
              Become a Family Wealth Strategist{" "}
              <em className="text-bronze-400 italic font-bold not-italic md:italic">
                & Economic Empowerment Coach.
              </em>
            </h1>
            <p className="mt-7 max-w-[480px] font-sans text-[17px] leading-[1.65] text-cream-50/88">
              Currency Thoughts is a Family Wealth Strategy Firm. We&rsquo;re building a
              team of Family Wealth Strategists who treat clients like family and serve as
              Economic Empowerment Coaches for the next generation.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <PrimaryButton asChild size="lg">
                <Link to="/careers/apply">Apply Now</Link>
              </PrimaryButton>
              <SecondaryButton asChild size="lg">
                <a href="#fit-quiz">Take the Fit Quiz</a>
              </SecondaryButton>
            </div>
          </div>
          <div className="lg:justify-self-end w-full max-w-[560px]">
            <div
              className="relative aspect-video bg-navy-800 ring-2 ring-bronze-600/70 flex flex-col items-center justify-center group cursor-pointer shadow-[0_30px_80px_-40px_rgba(0,0,0,0.6)]"
              style={{ borderRadius: "var(--radius-card, 4px)" }}
              role="button"
              aria-label="A word from our founder"
            >
              <div className="h-16 w-16 rounded-full bg-bronze-600/15 ring-1 ring-bronze-400/50 flex items-center justify-center transition-transform group-hover:scale-105">
                <Play className="h-7 w-7 text-bronze-400 ml-0.5" fill="currentColor" />
              </div>
              <span className="absolute bottom-5 left-0 right-0 text-center font-sans text-[11px] uppercase tracking-[0.22em] text-cream-50/70">
                — A Word From Our Founder —
              </span>
            </div>
          </div>
        </div>
      </FiligreeFrame>
    </section>
  );
}

function StatStrip() {
  const stats = [
    { title: "100%", subtitle: "Veteran Led" },
    { title: "$0", subtitle: "Startup Cost" },
    { title: "Built-In", subtitle: "Training Academy" },
    { title: "AI-Powered", subtitle: "Strategist Tools" },
  ];
  return (
    <section className="bg-navy-900 border-t border-bronze-600/30">
      <div className="container mx-auto px-6 md:px-10 py-16 md:py-20">
        <div className="grid grid-cols-2 gap-6 sm:gap-10 md:grid-cols-4 md:gap-12 justify-items-center">
          {stats.map((s) => (
            <div key={s.subtitle} className="min-w-0 w-full flex justify-center">
              <Seal title={s.title} subtitle={s.subtitle} size="md" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function QuizSection() {
  return (
    <section id="fit-quiz" className="bg-cream-50 py-24 md:py-28">
      <div className="container mx-auto px-6 md:px-10">
        <div className="text-center">
          <Eyebrow align="center">The Fit Quiz</Eyebrow>
          <h2 className="mt-6 font-display font-semibold text-navy-900 text-[36px] md:text-[44px] leading-[1.1] tracking-tight">
            Five honest questions.{" "}
            <em className="italic text-bronze-600">One real answer.</em>
          </h2>
          <p className="mt-5 max-w-[600px] mx-auto font-sans text-[16px] leading-[1.65] text-charcoal-900/85">
            Most recruiting pages tell you everything is amazing. We&rsquo;d rather
            help you figure out if this is actually right for you.
          </p>
        </div>
        <div className="mt-14">
          <AdvisorFitQuiz />
        </div>
      </div>
    </section>
  );
}

function FoundersLetter() {
  const f = useFounder();
  return (
    <section className="bg-cream-50 py-24 md:py-28 border-t border-bronze-600/20">
      <div className="container mx-auto px-6 md:px-10">
        <article className="mx-auto max-w-[720px]">
          <div className="flex justify-center">
            <Eyebrow align="center">A Letter From the Founder</Eyebrow>
          </div>
          <div className="mt-12 font-display text-charcoal-900 text-[19px] leading-[1.7] space-y-6 [&_p:first-of-type::first-letter]:float-left [&_p:first-of-type::first-letter]:font-display [&_p:first-of-type::first-letter]:font-bold [&_p:first-of-type::first-letter]:text-bronze-600 [&_p:first-of-type::first-letter]:text-[72px] [&_p:first-of-type::first-letter]:leading-[0.85] [&_p:first-of-type::first-letter]:mr-3 [&_p:first-of-type::first-letter]:mt-1">
            <p>
              I started Currency Thoughts after watching too many good people get
              chewed up by an industry that confuses noise for service. I came out
              of the military with one habit that stuck: take care of the people
              standing next to you, then build something that outlives you. That
              habit became this agency.
            </p>
            <p>
              Most insurance shops are still running the 1990&rsquo;s playbook —
              recruit hard, train light, push product, replace the agents who
              burn out. Families end up with a stranger&rsquo;s signature on a
              policy and no one to call when life changes. Strategists end up
              measuring success by appointments instead of impact. Everyone loses
              something, slowly.
            </p>
            <p>
              We built Currency Thoughts as the opposite. Real licensing support.
              An academy that treats financial literacy as a craft. AI tools that
              do the paperwork so our strategists can do the listening. Compliance
              that protects families before it protects the firm. No startup
              cost, no quotas designed to grind you down, no script.
            </p>
            <p>
              We are looking for strategists who measure their week in conversations,
              not closes — people who already think generationally, even if no
              one has ever paid them to. Veterans, second-career parents,
              educators, operators. Steady hands. Honest mouths.
            </p>
            <p>
              If this sounds like you, take the Fit Quiz above. Then book the call.
              I&rsquo;ll show up. I&rsquo;ll be honest with you about what this
              work asks and what it gives back. That&rsquo;s the only kind of
              conversation worth having.
            </p>
          </div>
          <div className="mt-12 pt-8 border-t border-bronze-600/30 text-center">
            {f?.founder_signature_url ? (
              <img
                src={f.founder_signature_url}
                alt={`${f.founder_name} signature`}
                className="mx-auto h-20 object-contain"
              />
            ) : f?.founder_name ? (
              <p
                className="text-bronze-600 text-[48px] md:text-[56px] leading-none"
                style={{ fontFamily: '"Allura", cursive' }}
              >
                {f.founder_name}
              </p>
            ) : null}
            <p className="mt-3 font-sans text-[11px] uppercase tracking-[0.22em] text-charcoal-500">
              {f?.founder_role ?? "Founder · Currency Thoughts LLC"}
            </p>
          </div>
        </article>
      </div>
    </section>
  );
}

function AboutOpportunity() {
  return (
    <section className="bg-cream-50 py-16 border-t border-bronze-600/20">
      <div className="container mx-auto px-6 md:px-10">
        <article className="mx-auto max-w-[720px]">
          <div className="flex justify-center">
            <Eyebrow align="center">About the opportunity</Eyebrow>
          </div>
          <h2 className="mt-6 text-center font-display font-semibold text-navy-800 text-[28px] md:text-[32px] leading-[1.2]">
            Honesty about what this is.
          </h2>
          <div className="mt-10 font-sans text-charcoal-900 text-[16px] leading-[1.65] space-y-5 [&_p:first-of-type::first-letter]:float-left [&_p:first-of-type::first-letter]:font-display [&_p:first-of-type::first-letter]:font-bold [&_p:first-of-type::first-letter]:text-bronze-600 [&_p:first-of-type::first-letter]:text-[72px] [&_p:first-of-type::first-letter]:leading-[0.85] [&_p:first-of-type::first-letter]:mr-3 [&_p:first-of-type::first-letter]:mt-1">
            <p>
              Currency Thoughts is an independent insurance agency. Our upline organization provides the licensing support, compliance oversight, and back-office infrastructure that lets us serve families across multiple states. We name that relationship, and every carrier relationship, directly in conversation with licensed candidates rather than in public advertising.
            </p>
            <p>
              Our strategists are independent contractors with their own state-issued life and health insurance licenses. We&rsquo;ll help you get yours if you&rsquo;re not licensed yet. Compensation is commission-based &mdash; there is no salary, but there is no income ceiling either. Strategists are paid by the carrier when policies they place go into force.
            </p>
            <p>
              Building a team of strategists is part of how Currency Thoughts grows. When you recruit a fellow strategist to the team, you earn overrides on their production as you continue to mentor them. This is how independent insurance distribution has worked for decades, and it is how we&rsquo;ll build something that lasts generations.
            </p>
            <p>
              The work requires real commitment: state licensing, ongoing training, and the willingness to have honest conversations with families about their financial protection. We don&rsquo;t promise easy money. We promise meaningful work, real income, and a brand that families and other strategists actually want to be part of.
            </p>
          </div>
        </article>
      </div>
      <div className="mt-12 container mx-auto px-6 md:px-10 max-w-[720px]">
        <div className="h-px bg-bronze-600/40 w-full" />
      </div>
    </section>
  );
}